/**
 * Deling (sak 232, T3): programpost, ressurs, vakt, avdeling og årgang, med en ekstern eller en
 * lenkeperson. Grensesnittet er fryst i `docs/qa/eksterne-deling/PLAN.md` §1.4.
 *
 * En deling ER fortsatt en `object_grants`-rad. Det nye er hvem den går til (`kind`), kontaktpersonen,
 * og at utløpet er påkrevd (232.5: «Ingen utløp» finnes ikke).
 *
 * ØKONOMI OG KONTRAKT DELES ALDRI (232.10). De er borte fra `SHAREABLE_TABS`, så et omfang som
 * navngir dem avvises av skjemaet, og `shareCovers` svarer nei på dem uansett hva en gammel rad sier.
 */
import { z } from 'zod';

import { emailDeliveryOutcomeSchema, messageDeliverySchema } from './messaging.js';
import { wireInstant } from './wire-time.js';

/** Det som kan deles. Speiler `object_type`-verdiene (0457 la til `resource`). */
export const SHARE_OBJECT_TYPES = [
  'performance',
  'resource',
  'shift',
  'department',
  'edition',
  /*
   * F14 (0640): kjøreplanen for én scene, én dag eller alle. §5.3 (1101): scenen samlet — kjøreplanen,
   * programpostene med teknisk advance, ressursen og vaktene — også til en ekstern, i én årgang.
   */
  'stage',
  /* F12 (0661): ett dokument (en fil i en mappe) og en mappe med undermappene. */
  'document',
  'folder',
  /* F13 (0661): årgangens kart og område. `objectId` er årgangen. */
  'edition_map',
  /* F18 (0622): salens spesifikasjon og arrangementet, fra spillestedet. Bare til eksterne. */
  'venue_room',
  'venue_night',
  /*
   * D4 (QA13 #100, 0936): ARTISTEN, delt med en ekstern manager. Objektet er byråets rad i
   * bookingregisteret (`booking_artists`). Bare til eksterne med innlogging, bare se og kommentere
   * (253.24), og omfanget er `ARTIST_SHARE_SCOPES`. Mottakeren leser den som et dokument fra
   * `app.shared_artist_view` — aldri byråets egne sider.
   */
  'artist',
] as const;
export const shareObjectTypeSchema = z.enum(SHARE_OBJECT_TYPES);
export type ShareObjectType = z.infer<typeof shareObjectTypeSchema>;

/** Det en lenkeperson kan få (spesifikasjonen §3: «i første omgang ressurs, vakt og programpost»). */
export const SHARE_LINK_OBJECT_TYPES = [
  'resource',
  'shift',
  'performance',
  /* F12 og F13: dokumenter, mapper og kartet kan også gå til en lenkeperson. */
  'document',
  'folder',
  'edition_map',
  /* F14: kjøreplanen for én scene. */
  'stage',
  /* 244.11 (0804): salens spesifikasjon og arrangementet fra spillestedet. */
  'venue_room',
  'venue_night',
] as const;
export function linkAllowedFor(type: ShareObjectType): boolean {
  return (SHARE_LINK_OBJECT_TYPES as readonly string[]).includes(type);
}

/**
 * DET SOM BARE DELES SOM LENKE. Tom siden §5.3 (27.10, 1101): scenen var den ene (F14, kjøreplanen),
 * og nå deles den samlet også med en ekstern med innlogging — kjøreplanen, programpostene med teknisk
 * advance, ressursen og vaktene.
 */
export const SHARE_LINK_ONLY_OBJECT_TYPES: readonly ShareObjectType[] = [];
export function externalAllowedFor(type: ShareObjectType): boolean {
  return !(SHARE_LINK_ONLY_OBJECT_TYPES as readonly string[]).includes(type);
}

/** Én festivaldag i kjøreplanens omfang: `day:2027-07-16`. Ingen dag er alle dagene. */
export const STAGE_DAY_SCOPE = /^day:\d{4}-\d{2}-\d{2}$/;
export function stageDayScope(day: string): string {
  return `day:${day}`;
}
export function stageDaysOf(scopes: readonly string[] | null): string[] {
  return (scopes ?? []).filter((scope) => STAGE_DAY_SCOPE.test(scope)).map((s) => s.slice(4));
}

/**
 * DET SOM BARE KAN LESES, UANSETT HVEM DET DELES MED (F12, F13). Et dokument, en mappe og kartet har
 * ingenting å kommentere eller endre for den som får dem: nivået er alltid «se», også for en ekstern.
 */
export const SHARE_VIEW_ONLY_OBJECT_TYPES = ['document', 'folder', 'edition_map'] as const;
export function shareIsViewOnly(type: ShareObjectType): boolean {
  return (SHARE_VIEW_ONLY_OBJECT_TYPES as readonly string[]).includes(type);
}

/** `external` logger inn i Tvitvi; `link` får en personlig lenke uten innlogging. */
export const SHARE_KINDS = ['external', 'link'] as const;
export const shareKindSchema = z.enum(SHARE_KINDS);
export type ShareKind = z.infer<typeof shareKindSchema>;

/** Fanene en delt programpost kan vise. Kontrakt og økonomi er ute for godt (232.10). */
export const SHAREABLE_TABS = [
  'overview',
  'people',
  'tasks',
  'travel',
  'stay',
  'advance',
  'chat',
] as const;
export type ShareableTab = (typeof SHAREABLE_TABS)[number];

/**
 * HVA EN MANAGER SER AV ARTISTEN (D4, 253.22). Kalenderen, bookingene, bud og honorar, dokumentene,
 * advancene og samtalen med byrået. Byråets provisjon, interne notater og internchat er ikke et omfang
 * og kan ikke bli det: de finnes ikke i `app.shared_artist_view`.
 */
export const ARTIST_SHARE_SCOPES = [
  'calendar',
  'bookings',
  'bids_fees',
  'documents',
  'advance',
  'chat',
] as const;
export type ArtistShareScope = (typeof ARTIST_SHARE_SCOPES)[number];
export function isArtistShareScope(value: string): value is ArtistShareScope {
  return (ARTIST_SHARE_SCOPES as readonly string[]).includes(value);
}
/**
 * BANDETS OMRÅDER (276.11 c, 1059): managementens uten «Bud og honorar» (A13). Basen nekter det
 * samme i bandets sett (`ck_booking_artists__band_share_scopes`).
 */
export const BAND_SHARE_SCOPES = [
  'calendar',
  'bookings',
  'documents',
  'advance',
  'chat',
] as const satisfies readonly ArtistShareScope[];
export type BandShareScope = (typeof BAND_SHARE_SCOPES)[number];
export function isBandShareScope(value: string): value is BandShareScope {
  return (BAND_SHARE_SCOPES as readonly string[]).includes(value);
}
/**
 * Se og endre (bestillingen §2.1 og §5.2, 27.10): «Kan kommentere» er borte. «Kan endre» (276.28 b,
 * 1060) er skrivetilgang i advancen for artistens konserter.
 */
export const ARTIST_SHARE_PERMISSIONS = ['view', 'edit'] as const;
export type ArtistSharePermission = (typeof ARTIST_SHARE_PERMISSIONS)[number];

/** Omfang som aldri gis, uansett hva som står på raden. */
const NEVER_SHARED: readonly string[] = ['contract', 'economy'];

export const MAX_SHARE_SCOPES = 40;
export const MAX_SHARE_NOTE = 500;
/** F10: hvor mange objekter én deling kan ta med seg i tillegg til det den åpnes fra. */
export const MAX_SHARE_BATCH = 50;

/** §5.2 (27.10): se og endre. Chatten følger av å se (`GUEST_ACTIONS`). */
export const SHARE_PERMISSIONS = ['view', 'edit'] as const;
export const sharePermissionSchema = z.enum(SHARE_PERMISSIONS);
export type SharePermission = z.infer<typeof sharePermissionSchema>;

/** Én fane, eller `advance:<uuid>` for én advance-seksjon. */
export const shareScopeSchema = z
  .string()
  .trim()
  .min(1)
  .max(64)
  .refine(
    (value) =>
      (SHAREABLE_TABS as readonly string[]).includes(value) ||
      isArtistShareScope(value) ||
      /^advance:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value) ||
      STAGE_DAY_SCOPE.test(value),
    { error: 'unknown_scope' },
  );

/** Advance-fanen eller én advance-seksjon (F17: det som kan deles videre fra den delte advancen). */
export function isAdvanceScope(scope: string): boolean {
  return scope === 'advance' || scope.startsWith('advance:');
}

const scopeList = z.array(shareScopeSchema).min(1).max(MAX_SHARE_SCOPES);
const note = z.string().trim().min(1).max(MAX_SHARE_NOTE).nullable();

/** En kjent person i registeret, eller en e-postadresse (ny kontakt lages i organisasjonen). */
export const shareRecipientSchema = z.union([
  z.strictObject({ personId: z.uuid() }),
  z.strictObject({
    email: z.email().max(320),
    displayName: z.string().trim().min(1).max(200).optional(),
  }),
]);
export type ShareRecipient = z.infer<typeof shareRecipientSchema>;

/**
 * EN LENKEPERSON PÅ AKKREDITERINGSLISTA (239.20). Standard er ikke på lista; den som deler velger det,
 * med nivå og passtype. Rollen er det lista sier hen er der som («Sjåfør», «Presse»).
 */
export const shareAccreditationSchema = z
  .object({
    roleLabel: z.string().trim().min(1).max(120),
    accreditationLevelId: z.uuid().nullable(),
    accreditationPassId: z.uuid().nullable(),
  })
  .meta({ id: 'ShareAccreditation' });
export type ShareAccreditation = z.infer<typeof shareAccreditationSchema>;

export const createShareRequestSchema = z
  .object({
    kind: shareKindSchema,
    recipient: shareRecipientSchema,
    /** En lenke kan bare leses — skjemaet nekter alt annet framfor å senke det stille. */
    permission: sharePermissionSchema,
    /**
     * Programpost: faner og advance-seksjoner. Scene (F14): festivaldagene, `day:YYYY-MM-DD`. Null er
     * hele programposten eller alle dagene; en tom liste avvises.
     */
    scopes: scopeList.nullable(),
    /** Påkrevd (232.5). Forhåndsutfylt fra `defaultExpiresAt` i dialogen. */
    expiresAt: z.iso.datetime(),
    contactPersonId: z.uuid().nullable(),
    /** Påkrevd for en lenke på en ressurs: lenken er per årgang (232.1), og ressursen er festivalens. */
    editionId: z.uuid().optional(),
    note,
    /**
     * Send lenken eller invitasjonen på SMS også (jobb 3, 0495), til mobilnummeret på personen i
     * registeret. Avvises med `sms_switched_off` når plattformbryteren er av, og med
     * `no_mobile_number` når personen ikke har et.
     */
    sendSms: z.boolean().optional(),
    /**
     * 260.10: mottakeren svarer Godta eller Avslå før tilgangen gjelder. Bare for en ny, ekstern deling
     * av en artist til en med konto («Gi innlogging» for bandet); en invitasjon er et ja i seg selv.
     */
    awaitAnswer: z.boolean().optional(),
    /**
     * 276.11 c (1059): en artistdeling til en i bandet som følger bandets sett. Basen setter omfanget
     * og nivået fra artisten; det som sendes i `scopes` og `permission`, teller ikke da.
     */
    followsBand: z.boolean().optional(),
    /** 239.20: bare for en lenke. Utelatt eller null er «ikke på akkrediteringslista». */
    accreditation: shareAccreditationSchema.nullable().optional(),
    /**
     * F10 · DEL FLERE PÅ ÉN GANG. Andre objekter av samme type som får hver sin deling med den samme
     * mottakeren, det samme nivået, omfanget, utløpet og meldingen. Ingen ny datamodell: det er
     * `object_grants`-rader som alle andre, så nye delbare typer virker uten mer arbeid.
     */
    alsoObjectIds: z.array(z.uuid()).max(MAX_SHARE_BATCH).optional(),
  })
  .refine((body) => body.kind !== 'link' || body.permission === 'view', {
    path: ['permission'],
    error: 'link_is_view_only',
  })
  .refine((body) => body.kind === 'link' || body.accreditation == null, {
    path: ['accreditation'],
    error: 'accreditation_only_for_link',
  })
  /*
   * 244.14: en lenke på en programpost kan også snevres til Advance og advance-seksjonene (rideren ja,
   * hospitality nei). Den delte advancen videre fra motparten (F17) er fortsatt aldri en lenke — det
   * vokter `advance-shares` og basen (0621), ikke dette skjemaet.
   */
  /* En advance-seksjon er én programposts egen, og kan ikke følge med til de andre. */
  .refine(
    (body) =>
      (body.alsoObjectIds ?? []).length === 0 ||
      !(body.scopes ?? []).some((scope) => scope.startsWith('advance:')),
    { path: ['scopes'], error: 'section_scope_not_for_many' },
  )
  .meta({ id: 'CreateShareRequest' });
export type CreateShareRequest = z.infer<typeof createShareRequestSchema>;

export const updateShareRequestSchema = z
  .object({
    permission: sharePermissionSchema.optional(),
    scopes: scopeList.nullable().optional(),
    expiresAt: z.iso.datetime().optional(),
    contactPersonId: z.uuid().nullable().optional(),
    note: note.optional(),
    /** 276.11 c (1059): følg bandets sett (true) eller egne avhukinger (false). */
    followsBand: z.boolean().optional(),
  })
  .meta({ id: 'UpdateShareRequest' });
export type UpdateShareRequest = z.infer<typeof updateShareRequestSchema>;

export const shareDtoSchema = z
  .object({
    id: z.uuid(),
    objectType: shareObjectTypeSchema,
    objectId: z.uuid(),
    kind: shareKindSchema,
    personId: z.uuid(),
    displayName: z.string(),
    permission: sharePermissionSchema,
    scopes: z.array(z.string()).nullable(),
    expiresAt: wireInstant.nullable(),
    /** Regnet av serveren, så «utløpt» er ett svar og ikke hver klients egen klokke. */
    expired: z.boolean(),
    contactPersonId: z.uuid().nullable(),
    contactName: z.string().nullable(),
    grantedByName: z.string().nullable(),
    createdAt: wireInstant,
    firstOpenedAt: wireInstant.nullable(),
    lastOpenedAt: wireInstant.nullable(),
    confirmedAt: wireInstant.nullable(),
    /** 232.2: «I delingen står når lenken sist ble laget og av hvem.» Null for en ekstern. */
    linkCreatedAt: wireInstant.nullable(),
    linkCreatedByName: z.string().nullable(),
    /** 239.32: en trukket deling står i panelet, gråtonet. Null for en levende. */
    revokedAt: wireInstant.nullable().default(null),
    revokedByName: z.string().nullable().default(null),
    /**
     * F17 (0621): organisasjonen som delte, når det er den ANDRE parten i en delt advance (byrået som
     * deler festivalens advance videre). Null når delingen er organisasjonens egen.
     */
    grantedByOrganizationName: z.string().nullable().optional(),
    /** Jobb 2 (0491): e-posten med lenken eller invitasjonen. Null når ingenting er sendt. */
    delivery: messageDeliverySchema.nullable(),
    /** F1: meldingen til mottakeren. Den står i tråden og på lenkesiden. */
    note: z.string().nullable().default(null),
    /**
     * F4: om en ekstern har innlogging (`linked`), en konto invitasjonen kobler til (`email`),
     * eller ingen (`none`). Null for en lenke.
     */
    account: z.enum(['linked', 'email', 'none']).nullable().default(null),
    /** F4: utløpet på den åpne invitasjonen til en ekstern uten konto. Null når ingen er åpen. */
    invitationExpiresAt: wireInstant.nullable().default(null),
    /** 260.10: delingen venter på mottakerens Godta eller Avslå, og gir ingen tilgang ennå. */
    awaitingAnswer: z.boolean().default(false),
    /** 276.11 c (1059): artistdelingen følger bandets sett. */
    followsBand: z.boolean().default(false),
  })
  .meta({ id: 'Share' });
export type ShareDto = z.infer<typeof shareDtoSchema>;

export const shareListResponseSchema = z
  .object({
    items: z.array(shareDtoSchema),
    /** 232.5: 14 dager etter at objektet er ferdig — `app.share_default_expiry`. */
    defaultExpiresAt: wireInstant,
  })
  .meta({ id: 'ShareListResponse' });
export type ShareListResponse = z.infer<typeof shareListResponseSchema>;

export const SHARE_OUTCOMES = ['granted', 'invited', 'link_created', 'link_exists'] as const;
export type ShareOutcome = (typeof SHARE_OUTCOMES)[number];

/** Tokenene finnes i nøyaktig dette ene svaret; basen har bare hashen. */
export const shareCreatedResponseSchema = z
  .object({
    share: shareDtoSchema,
    outcome: z.enum(SHARE_OUTCOMES),
    invitationToken: z.string().optional(),
    linkToken: z.string().optional(),
    /** F10: delingene som ble laget for `alsoObjectIds`, i samme rekkefølge. */
    alsoShareIds: z.array(z.uuid()).optional(),
    /**
     * 263.13: hva som skjer med e-posten bak invitasjonen eller lenken, sagt før den er lagt i køen
     * (`emailDelivery`, som invitasjonenes kvittering). Bare med `invited` og `link_created`.
     */
    emailDelivery: emailDeliveryOutcomeSchema.optional(),
  })
  .meta({ id: 'ShareCreatedResponse' });
export type ShareCreatedResponse = z.infer<typeof shareCreatedResponseSchema>;

/** «Lag ny lenke» (232.2): den gamle slutter å virke, og den nye vises én gang. */
export const shareLinkResponseSchema = z
  .object({ token: z.string().min(1) })
  .meta({ id: 'ShareLinkResponse' });
export type ShareLinkResponse = z.infer<typeof shareLinkResponseSchema>;

/**
 * F4 · NY INVITASJONSLENKE til en ekstern uten konto. Den gamle slutter å virke, e-posten sendes på
 * nytt, og lenken står i dette svaret én gang. `has_account`: personen har fått konto i mellomtiden.
 */
export const shareInvitationResponseSchema = z
  .object({
    outcome: z.enum(['invited', 'has_account']),
    token: z.string().optional(),
    expiresAt: z.string().optional(),
  })
  .meta({ id: 'ShareInvitationResponse' });
export type ShareInvitationResponse = z.infer<typeof shareInvitationResponseSchema>;

/** F6: hvor mange levende delinger et objekt har — advarselen før det slettes. */
export const shareCountResponseSchema = z
  .object({ live: z.number().int().nonnegative() })
  .meta({ id: 'ShareCountResponse' });
export type ShareCountResponse = z.infer<typeof shareCountResponseSchema>;

/** F10: et annet objekt av samme type som kan deles i samme slengen. */
export const shareSiblingSchema = z
  .object({
    id: z.uuid(),
    title: z.string(),
    startsAt: z.string().nullable(),
    endsAt: z.string().nullable(),
    /** Avdelingen, scenen eller typen — det som skiller to med samme navn. */
    detail: z.string().nullable(),
  })
  .meta({ id: 'ShareSibling' });
export type ShareSibling = z.infer<typeof shareSiblingSchema>;

export const shareSiblingListSchema = z
  .object({ items: z.array(shareSiblingSchema) })
  .meta({ id: 'ShareSiblingList' });
export type ShareSiblingList = z.infer<typeof shareSiblingListSchema>;

/**
 * F8 · DELINGSKORTENE I TRÅDEN FOR DEN SOM DELTE. Delingen slik organisasjonen ser den, og det delte
 * objektet med det kortet trenger for å lenke dit.
 */
export const threadShareSchema = z
  .object({
    share: shareDtoSchema,
    title: z.string(),
    startsAt: z.string().nullable(),
    endsAt: z.string().nullable(),
    festivalId: z.uuid().nullable(),
    editionId: z.uuid().nullable(),
    /** `venue` lenker til spillestedets sider. */
    festivalKind: z.string().nullable(),
  })
  .meta({ id: 'ThreadShare' });
export type ThreadShare = z.infer<typeof threadShareSchema>;

export const threadShareListSchema = z
  .object({ items: z.array(threadShareSchema) })
  .meta({ id: 'ThreadShareList' });
export type ThreadShareList = z.infer<typeof threadShareListSchema>;

/**
 * Om en deling viser én fane på programposten. Delt, fordi API-et og siden må svare likt.
 *
 * Null er hele programposten — men hele programposten er ALDRI kontrakten eller økonomien (232.10).
 */
export function shareCovers(scopes: readonly string[] | null, tab: string): boolean {
  if (NEVER_SHARED.includes(tab)) return false;
  if (scopes === null) return true;
  if (scopes.includes(tab)) return true;
  /* Et seksjonsomfang innebærer Advance-fanen det bor i, og ingenting annet. */
  return tab === 'advance' && scopes.some((scope) => scope.startsWith('advance:'));
}
